// Session-telemetry recorder (ED-2570/ED-2572) — the app-wide singleton that
// turns usage into a PHI-free journey stream: screen changes, API timings
// (fetch + NDJSON streaming), scrubbed errors, named product actions,
// lifecycle marks. Buffered and batch-POSTed to
// /telemetry/{account}/{cognito} (see backend controllers/telemetry).
//
// Design constraints, in order:
//   1. NEVER break the app — every public method is try/caught, uploads are
//      fire-and-forget, failures back off and eventually drop data.
//   2. NEVER capture content — names/routes/durations only; scrub.ts strips
//      emails/uuids/digit-runs before anything leaves the process.
//   3. Be gentle on the API — the client HTTP API has a 10 req/s stage-wide
//      throttle, so flushes are batched (30s cadence or 60-event threshold)
//      and back off exponentially on failure.
//
// Durability (ED-2572): undelivered batches persist to an AsyncStorage
// outbox WITH their session meta (./outbox). The next launch drains them
// under their original session ids — the server's ownership guard accepts
// same-user claims and (session_id, seq) dedupes partial deliveries — so an
// app kill or a dead spot loses at most what arrived after the last
// persistence point (flush failure, backgrounding, or stop).
//
// Store/navigation access is lazy-require (errorReporting.ts idiom) so this
// module can be imported from anywhere — including apiClient via apiTrace —
// without creating a require cycle.
//
// Lifecycle: hooks/useSessionTelemetry.ts calls configure() when the
// session_telemetry_enabled flag + an authenticated session are both
// present, and stop() otherwise. App.tsx forwards navigation state changes;
// utilities/errorReporting.ts mirrors errors; utilities/apiClient.ts times
// requests through apiTrace.ts; the stream* modules report through
// apiTrace.traceStreamCall.

import { AppState, Platform } from 'react-native';
import type { AppStateStatus, NativeEventSubscription } from 'react-native';
import { isWeb } from '../../utilities/platform';
import { newSessionId, scrubText, templatePath } from './scrub';
import { startReplay, stopReplay } from './replay';
import {
  loadOutbox,
  removeEnvelope,
  saveOutbox,
  upsertEnvelope,
  type OutboxEnvelope,
  type OutboxSessionMeta,
} from './outbox';

export type TelemetryEventType = 'screen' | 'api' | 'error' | 'action' | 'lifecycle';

export interface TelemetryEvent {
  seq: number;
  ts: string;
  type: TelemetryEventType;
  name: string;
  screen?: string | null;
  ms?: number | null;
  status?: number | null;
  detail?: Record<string, unknown> | null;
}

const FLUSH_INTERVAL_MS = 30_000;
const FLUSH_THRESHOLD = 60;
const MAX_BATCH = 150; // server accepts 200
const MAX_BUFFER = 400; // oldest dropped beyond this
const MAX_EVENTS_PER_SESSION = 10_000; // runaway-loop backstop
const SESSION_IDLE_ROTATE_MS = 30 * 60_000;
const MAX_BACKOFF_TICKS = 8;

interface RecorderIds {
  accountId: string;
  cognitoId: string;
}

interface SendOutcome {
  ok: boolean;
  // A definitive server "no" (4xx: not the owner, bad payload) — retrying
  // the identical batch can never succeed, so callers should discard it.
  permanent: boolean;
}

class SessionTelemetryRecorder {
  private enabled = false;
  private replayWanted = false;
  private ids: RecorderIds | null = null;

  private sessionId = '';
  private sessionStartedAt = '';
  private seq = 0;
  private recordedThisSession = 0;
  private buffer: TelemetryEvent[] = [];
  private lastScreenName: string | null = null;

  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private flushing = false;
  private failStreak = 0;
  private skipTicks = 0;

  private appStateSub: NativeEventSubscription | null = null;
  private backgroundedAtMs: number | null = null;

  private outbox: OutboxEnvelope[] = [];
  private outboxRestored = false;
  private draining = false;
  private anonymous = false;

  // ── lifecycle ───────────────────────────────────────────────────────────

  configure(opts: { accountId: string; cognitoId: string; replay: boolean }): void {
    try {
      const idsChanged =
        this.ids?.accountId !== opts.accountId || this.ids?.cognitoId !== opts.cognitoId;
      const replayChanged = this.replayWanted !== opts.replay;
      this.replayWanted = opts.replay;

      if (this.enabled && this.anonymous) {
        // Sign-in during an anonymous (pre-login) session: CLAIM it — same
        // session id, same buffer, no rotation. Flushes switch to the
        // authenticated ingest, whose guarded upsert fills identity onto the
        // session and backfills the pre-auth event rows. The journey spans
        // landing → registering → first real use as ONE session.
        this.ids = { accountId: opts.accountId, cognitoId: opts.cognitoId };
        this.anonymous = false;
        this.syncReplay();
        void this.restoreAndDrainOutbox();
        void this.flush();
        return;
      }
      if (this.enabled && !idsChanged) {
        // Only the replay flag moved — start/stop the visual layer in place.
        if (replayChanged) this.syncReplay();
        return;
      }
      if (this.enabled && idsChanged) this.stop('identity_changed');

      this.ids = { accountId: opts.accountId, cognitoId: opts.cognitoId };
      this.anonymous = false;
      this.enabled = true;
      this.startSession('session_start');
      this.ensureRuntime();
      void this.restoreAndDrainOutbox();
    } catch {
      // configuration must never throw into React
    }
  }

  /**
   * Pre-login capture (ED-2570 follow-up #3) — starts an ANONYMOUS session
   * gated by the public-settings pair (session_telemetry_enabled +
   * session_telemetry_prelogin_enabled; see useSessionTelemetry). Batches go
   * to the public /telemetry/anonymous ingest with no identity; the first
   * configure() after sign-in claims the session in place. Visual replay
   * never runs anonymously (no DOM recording of credential screens).
   */
  configureAnonymous(): void {
    try {
      if (this.enabled) return; // already capturing (authed or anonymous)
      this.ids = null;
      this.anonymous = true;
      this.enabled = true;
      this.replayWanted = false;
      this.startSession('session_start');
      this.ensureRuntime();
    } catch {
      // never throw into React
    }
  }

  private ensureRuntime(): void {
    if (!this.flushTimer) {
      this.flushTimer = setInterval(() => this.tick(), FLUSH_INTERVAL_MS);
    }
    if (!this.appStateSub) {
      this.appStateSub = AppState.addEventListener('change', (s) => this.onAppState(s));
    }
  }

  stop(_reason: string): void {
    try {
      if (!this.enabled) return;
      this.enabled = false;
      stopReplay();
      // Persist the pre-flush buffer first — if the force-flush below wins
      // the race it re-syncs the (then empty) envelope right after.
      this.persistOwnEnvelope();
      void this.flush(true);
      if (this.flushTimer) {
        clearInterval(this.flushTimer);
        this.flushTimer = null;
      }
      if (this.appStateSub) {
        this.appStateSub.remove();
        this.appStateSub = null;
      }
      this.lastScreenName = null;
    } catch {
      // never throw
    }
  }

  // ── capture entry points ────────────────────────────────────────────────

  /** App.tsx onStateChange — derive the active route from navigationRef. */
  onNavigationStateChange(): void {
    if (!this.enabled) return;
    const name = this.currentScreen();
    if (!name || name === this.lastScreenName) return;
    this.lastScreenName = name;
    this.push('screen', name.slice(0, 120), { screen: name.slice(0, 120) });
  }

  /** apiTrace.ts — one timed API call. `url` may still carry origin + ids. */
  recordApi(url: string, method: string, status: number, ms: number): void {
    if (!this.enabled) return;
    const path = templatePath(String(url).replace(/^https?:\/\/[^/]+/, ''));
    if (!path) return;
    this.push('api', `${String(method).toUpperCase().slice(0, 8)} ${path}`, {
      ms: Math.max(0, Math.round(ms)),
      status: Number.isFinite(status) ? status : 0,
    });
  }

  /** apiTrace.traceStreamCall — one completed NDJSON/XHR streaming request. */
  recordStream(kind: string, status: number, ms: number, ttfbMs: number | null): void {
    if (!this.enabled) return;
    const label = String(kind).replace(/[^a-z0-9-]/gi, '').slice(0, 40);
    if (!label) return;
    this.push('api', `STREAM /${label}`, {
      ms: Math.max(0, Math.round(ms)),
      status: Number.isFinite(status) ? status : 0,
      detail: ttfbMs != null ? { ttfb_ms: Math.max(0, Math.round(ttfbMs)) } : null,
    });
  }

  /** errorReporting.ts — mirrors every reported client error. */
  recordError(name: string, message: string, source: string): void {
    if (!this.enabled) return;
    const label = scrubText(`${name}: ${message}`, 300);
    if (!label) return;
    this.push('error', label, { detail: { source } });
  }

  /** Explicit product instrumentation ("plan_created", "note_created", …). */
  track(name: string, detail?: Record<string, unknown>): void {
    if (!this.enabled) return;
    const label = scrubText(name, 120);
    if (!label) return;
    this.push('action', label, { detail });
  }

  // ── internals ───────────────────────────────────────────────────────────

  private startSession(mark: 'session_start' | 'session_resume'): void {
    this.sessionId = newSessionId();
    this.sessionStartedAt = new Date().toISOString();
    this.seq = 0;
    this.recordedThisSession = 0;
    this.buffer = [];
    this.failStreak = 0;
    this.skipTicks = 0;
    this.push('lifecycle', mark);
    // Seed the journey with the screen the user is already on.
    this.lastScreenName = null;
    this.onNavigationStateChange();
    this.syncReplay();
  }

  private rotateSession(): void {
    // Ship the old session's tail with its own meta before re-keying; if the
    // send fails, sendBatch has already parked it in the outbox.
    const tail = this.buffer.splice(0, this.buffer.length);
    if (tail.length) {
      const meta = this.sessionMeta();
      void this.sendBatch(meta, tail, this.ids, { parkOnFailure: true });
    }
    this.syncOwnEnvelope();
    stopReplay();
    this.startSession('session_resume');
  }

  private syncReplay(): void {
    if (this.enabled && this.replayWanted && isWeb && this.ids) {
      const ids = this.ids;
      const meta = this.sessionMeta();
      startReplay({
        sessionId: this.sessionId,
        upload: async (chunkSeq, startedAtISO, events) => {
          const res = await this.post(`/telemetry/replay/${ids.accountId}/${ids.cognitoId}`, {
            session: meta,
            chunk_seq: chunkSeq,
            chunk_started_at: startedAtISO,
            events,
          });
          if (res?.disabled) {
            this.replayWanted = false;
            stopReplay();
            return true; // treat as delivered — the server said "stop sending"
          }
          return !!res?.success;
        },
      });
    } else {
      stopReplay();
    }
  }

  private onAppState(state: AppStateStatus): void {
    try {
      if (!this.enabled) return;
      if (state === 'background') {
        this.backgroundedAtMs = Date.now();
        this.push('lifecycle', 'background');
        // Persist BEFORE the flush races the OS suspending us — success
        // re-syncs the envelope down to whatever remains.
        this.persistOwnEnvelope();
        void this.flush(true);
      } else if (state === 'active' && this.backgroundedAtMs != null) {
        const away = Date.now() - this.backgroundedAtMs;
        this.backgroundedAtMs = null;
        if (away > SESSION_IDLE_ROTATE_MS) this.rotateSession();
        else this.push('lifecycle', 'foreground');
      }
    } catch {
      // never throw
    }
  }

  private currentScreen(): string | null {
    try {
      // Lazy — same idiom as errorReporting.sessionContext (no import cycle,
      // tolerates navigation not being mounted yet).
      const { navigationRef } = require('../../navigation/navigationRef');
      if (navigationRef?.isReady?.()) return navigationRef.getCurrentRoute()?.name ?? null;
    } catch {
      // navigation not available (tests, module-init)
    }
    return null;
  }

  private push(
    type: TelemetryEventType,
    name: string,
    extra?: { screen?: string | null; ms?: number | null; status?: number | null; detail?: Record<string, unknown> | null },
  ): void {
    try {
      if (this.recordedThisSession >= MAX_EVENTS_PER_SESSION) return;
      this.recordedThisSession += 1;
      this.buffer.push({
        seq: this.seq++,
        ts: new Date().toISOString(),
        type,
        name,
        screen: extra?.screen ?? (type === 'screen' ? name : this.lastScreenName),
        ms: extra?.ms ?? null,
        status: extra?.status ?? null,
        detail: extra?.detail ?? null,
      });
      if (this.buffer.length > MAX_BUFFER) this.buffer.splice(0, this.buffer.length - MAX_BUFFER);
      if (this.buffer.length >= FLUSH_THRESHOLD) void this.flush();
    } catch {
      // never throw
    }
  }

  private tick(): void {
    if (this.skipTicks > 0) {
      this.skipTicks -= 1;
      return;
    }
    void this.drainOutbox();
    void this.flush();
  }

  private sessionMeta(): OutboxSessionMeta {
    let timezone: string | null = null;
    let locale: string | null = null;
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
      locale = Intl.DateTimeFormat().resolvedOptions().locale ?? null;
    } catch {
      // Intl unavailable — meta stays null
    }
    // Lazy — devicePlatform pulls expo-constants, which many test suites
    // (and any module importing the recorder for track()) must not be
    // forced to transitively load. Same rationale as the store/navigation
    // lookups; the server defaults platform to 'unknown' anyway.
    let platform = 'unknown';
    let appVersion: string | null = null;
    try {
      const { getAppVersion, getPlatformDevice } = require('../../utilities/devicePlatform');
      platform = getPlatformDevice();
      appVersion = getAppVersion();
    } catch {
      // device metadata unavailable — meta stays coarse
    }
    return {
      id: this.sessionId,
      platform,
      app_version: appVersion,
      os_version: String(Platform.Version ?? ''),
      timezone,
      locale,
      started_at: this.sessionStartedAt,
    };
  }

  private async post(path: string, body: unknown): Promise<any> {
    // Lazy — apiClient imports apiTrace which imports this module; requiring
    // at call time keeps module init acyclic. apiClient's tracedFetch skips
    // /telemetry/ paths so telemetry never times itself.
    const { apiPost } = require('../../utilities/apiClient');
    return apiPost(path, body);
  }

  // Anonymous batches bypass apiClient entirely: pre-login there is no
  // token, and apiPost's refresh machinery must not run (it can dispatch a
  // sign-out on a cold start). Plain fetch, errorReporting precedent.
  private async postAnonymous(body: unknown): Promise<any> {
    const config = require('../../config').default;
    const res = await fetch(`${config.apiGateway.endpoint}/telemetry/anonymous`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    try {
      return await res.json();
    } catch {
      return { success: res.ok, status: res.status };
    }
  }

  private async sendBatch(
    meta: OutboxSessionMeta,
    events: TelemetryEvent[],
    ids: RecorderIds | null,
    opts: { parkOnFailure?: boolean } = {},
  ): Promise<SendOutcome> {
    try {
      const payload = { session: meta, events };
      const res = ids
        ? await this.post(`/telemetry/${ids.accountId}/${ids.cognitoId}`, payload)
        : await this.postAnonymous(payload);
      if (res?.disabled) {
        // Server-side kill switch flipped — drop everything and go quiet
        // until reconfigured; resending is pointless by definition.
        this.outbox = [];
        saveOutbox(this.outbox);
        this.stop('server_disabled');
        return { ok: true, permanent: false };
      }
      if (res?.success) return { ok: true, permanent: false };
      const status = Number(res?.status);
      const permanent = Number.isFinite(status) && status >= 400 && status < 500;
      if (!permanent && opts.parkOnFailure) this.parkEnvelope(meta, events);
      return { ok: false, permanent };
    } catch {
      if (opts.parkOnFailure) this.parkEnvelope(meta, events);
      return { ok: false, permanent: false };
    }
  }

  private async flush(force = false): Promise<void> {
    if (this.flushing) return;
    if (!this.buffer.length) return;
    if (!this.enabled && !force) return;
    this.flushing = true;
    try {
      const batch = this.buffer.slice(0, MAX_BATCH);
      const outcome = await this.sendBatch(this.sessionMeta(), batch, this.ids);
      if (outcome.ok || outcome.permanent) {
        // Delivered — or definitively refused, where retrying can't help.
        this.buffer.splice(0, batch.length);
        this.failStreak = 0;
        this.skipTicks = 0;
      } else {
        this.failStreak += 1;
        this.skipTicks = Math.min(2 ** this.failStreak, MAX_BACKOFF_TICKS);
      }
      this.syncOwnEnvelope();
    } finally {
      this.flushing = false;
    }
  }

  // ── outbox (ED-2572) ────────────────────────────────────────────────────

  /** Mirror the live buffer into persistent storage under this session. */
  private persistOwnEnvelope(): void {
    try {
      if (!this.sessionId) return;
      if (this.buffer.length) {
        this.outbox = upsertEnvelope(this.outbox, {
          meta: this.sessionMeta(),
          events: this.buffer.slice(),
        });
      } else {
        this.outbox = removeEnvelope(this.outbox, this.sessionId);
      }
      saveOutbox(this.outbox);
    } catch {
      // persistence is best-effort
    }
  }

  private syncOwnEnvelope(): void {
    this.persistOwnEnvelope();
  }

  /** Park a foreign/rotated batch for a later drain attempt. */
  private parkEnvelope(meta: OutboxSessionMeta, events: TelemetryEvent[]): void {
    try {
      const existing = this.outbox.find((e) => e.meta.id === meta.id);
      const merged = existing ? [...existing.events, ...events] : events;
      this.outbox = upsertEnvelope(this.outbox, { meta, events: merged });
      saveOutbox(this.outbox);
    } catch {
      // best-effort
    }
  }

  private async restoreAndDrainOutbox(): Promise<void> {
    try {
      if (!this.outboxRestored) {
        this.outboxRestored = true;
        const persisted = await loadOutbox();
        // Merge behind anything parked since boot; persisted state is older.
        const parkedIds = new Set(this.outbox.map((e) => e.meta.id));
        this.outbox = [...persisted.filter((e) => !parkedIds.has(e.meta.id)), ...this.outbox];
      }
      await this.drainOutbox();
    } catch {
      // never throw into configure
    }
  }

  private async drainOutbox(): Promise<void> {
    if (this.draining || !this.ids || !this.enabled) return;
    this.draining = true;
    try {
      // Foreign envelopes only — the live session's envelope mirrors the
      // buffer and is delivered by flush().
      const foreign = this.outbox.filter((e) => e.meta.id !== this.sessionId);
      for (const envelope of foreign) {
        const outcome = await this.sendBatch(envelope.meta, envelope.events, this.ids);
        if (!this.enabled) return; // server_disabled mid-drain
        if (outcome.ok || outcome.permanent) {
          this.outbox = removeEnvelope(this.outbox, envelope.meta.id);
          saveOutbox(this.outbox);
        } else {
          return; // transient failure — keep the rest for the next tick
        }
      }
    } finally {
      this.draining = false;
    }
  }
}

export const recorder = new SessionTelemetryRecorder();
